'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

export default function Footer() {
  const router = useRouter();
  const pathname = usePathname();

  const handleSectionLink = (sectionId: string) => {
    if (pathname === '/home' || pathname === '/') {
      scrollToSection(sectionId);
    } else {
      router.push(`/home?section=${sectionId}`);
    }
  };

  return (
    <footer style={{ background: '#0A0A0A', borderTop: '1px solid #1C1C1C' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col md:flex-row items-start justify-between gap-8">
          {/* Left: Brand */}
          <div className="flex-shrink-0">
            <span
              className="text-white"
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: '24px',
                letterSpacing: '4px',
              }}
            >
              ALLYWRK
            </span>
            <p
              className="mt-3 max-w-[220px] leading-relaxed"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
              }}
            >
              Media Production — South West Florida
            </p>
            <div className="flex items-center gap-4 mt-5">
              <a
                href="https://www.instagram.com/allywrk"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="transition-colors duration-300"
                style={{ color: 'rgba(255,255,255,0.4)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                </svg>
              </a>
              <a
                href="#"
                aria-label="TikTok"
                className="transition-colors duration-300"
                style={{ color: 'rgba(255,255,255,0.4)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
              >
                <svg width="16" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Links */}
          <div className="flex flex-wrap gap-x-12 gap-y-3">
            {[
              { label: 'Home', href: '/home', section: null },
              { label: 'Services', href: null, section: 'services' },
              { label: 'Work', href: null, section: 'work' },
              { label: 'About', href: null, section: 'about' },
              { label: 'Contact', href: null, section: 'contact' },
            ]?.map((link) =>
              link.section ? (
                <button
                  key={link.label}
                  onClick={() => handleSectionLink(link.section!)}
                  className="transition-colors duration-300 text-left"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '12px',
                    letterSpacing: '2px',
                    color: 'rgba(255,255,255,0.4)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  href={link.href!}
                  className="transition-colors duration-300"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '12px',
                    letterSpacing: '2px',
                    color: 'rgba(255,255,255,0.4)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
        </div>

        {/* Bottom row */}
        <div
          className="mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid #1C1C1C' }}
        >
          <p
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.25)',
              letterSpacing: '1px',
            }}
          >
            © 2026 ALLYWRK. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {['Privacy', 'Terms']?.map((item) => (
              <a
                key={item}
                href="#"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.25)',
                  letterSpacing: '1px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.25)')}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}